import { stockDataService, SymbolSearchResult } from '../api/stockData.service';
import { smartCache } from '../cache/smartCache';
import { getLatestStockSearch } from '../history/searchHistoryService';
import { llmTickerExtractor } from '../ai/llmTickerExtractor';
import { supabase } from '../supabase/supabaseClient';

/**
 * Ticker Detection Result
 */
export interface TickerDetectionResult {
  ticker: string | null;
  companyName: string;
  confidence: number; // 0-1 score
  suggestions: SymbolSearchResult[]; // Alternative matches if ambiguous
  source?: 'cache' | 'history' | 'common' | 'explicit' | 'llm' | 'api'; // Where the result came from
  allTickers?: string[]; // For multi-ticker support (group chats)
}

/**
 * Ticker Detection Service
 * Intelligently extracts stock tickers from natural language queries
 *
 * Hybrid Strategy (Fast → Smart → Fallback):
 * 1. Check cache (10ms)
 * 2. Check user's search history (50ms)
 * 3. Check common company name mappings (instant)
 * 4. Check explicit ticker patterns ($TSLA, TSLA) (instant)
 * 5. LLM-based extraction (200-500ms) 🆕
 * 6. Fallback to Alpha Vantage symbol search (1-2s)
 */
class TickerDetectionService {
  // Common company name → ticker mappings for instant detection
  private readonly commonMappings: Record<string, { ticker: string; name: string }> = {
    // FAANG
    tesla: { ticker: 'TSLA', name: 'Tesla, Inc.' },
    apple: { ticker: 'AAPL', name: 'Apple Inc.' },
    microsoft: { ticker: 'MSFT', name: 'Microsoft Corporation' },
    amazon: { ticker: 'AMZN', name: 'Amazon.com, Inc.' },
    google: { ticker: 'GOOGL', name: 'Alphabet Inc.' },
    alphabet: { ticker: 'GOOGL', name: 'Alphabet Inc.' },
    meta: { ticker: 'META', name: 'Meta Platforms, Inc.' },
    facebook: { ticker: 'META', name: 'Meta Platforms, Inc.' },
    netflix: { ticker: 'NFLX', name: 'Netflix, Inc.' },

    // Tech giants
    nvidia: { ticker: 'NVDA', name: 'NVIDIA Corporation' },
    amd: { ticker: 'AMD', name: 'Advanced Micro Devices, Inc.' },
    intel: { ticker: 'INTC', name: 'Intel Corporation' },
    salesforce: { ticker: 'CRM', name: 'Salesforce, Inc.' },
    oracle: { ticker: 'ORCL', name: 'Oracle Corporation' },
    adobe: { ticker: 'ADBE', name: 'Adobe Inc.' },

    // Finance
    jpmorgan: { ticker: 'JPM', name: 'JPMorgan Chase & Co.' },
    'jp morgan': { ticker: 'JPM', name: 'JPMorgan Chase & Co.' },
    'goldman sachs': { ticker: 'GS', name: 'Goldman Sachs Group, Inc.' },
    'bank of america': { ticker: 'BAC', name: 'Bank of America Corporation' },
    'wells fargo': { ticker: 'WFC', name: 'Wells Fargo & Company' },

    // Retail & Consumer
    walmart: { ticker: 'WMT', name: 'Walmart Inc.' },
    target: { ticker: 'TGT', name: 'Target Corporation' },
    costco: { ticker: 'COST', name: 'Costco Wholesale Corporation' },
    nike: { ticker: 'NKE', name: 'NIKE, Inc.' },
    starbucks: { ticker: 'SBUX', name: 'Starbucks Corporation' },
    'coca cola': { ticker: 'KO', name: 'The Coca-Cola Company' },
    'coca-cola': { ticker: 'KO', name: 'The Coca-Cola Company' },
    pepsi: { ticker: 'PEP', name: 'PepsiCo, Inc.' },

    // Healthcare & Pharma
    pfizer: { ticker: 'PFE', name: 'Pfizer Inc.' },
    'johnson & johnson': { ticker: 'JNJ', name: 'Johnson & Johnson' },
    moderna: { ticker: 'MRNA', name: 'Moderna, Inc.' },

    // Auto & Transport
    ford: { ticker: 'F', name: 'Ford Motor Company' },
    'general motors': { ticker: 'GM', name: 'General Motors Company' },
    gm: { ticker: 'GM', name: 'General Motors Company' },

    // Media & Entertainment
    disney: { ticker: 'DIS', name: 'The Walt Disney Company' },
    'warner bros': { ticker: 'WBD', name: 'Warner Bros. Discovery, Inc.' },
    spotify: { ticker: 'SPOT', name: 'Spotify Technology S.A.' },

    // Indices (common references)
    sp500: { ticker: '^GSPC', name: 'S&P 500' },
    's&p500': { ticker: '^GSPC', name: 'S&P 500' },
    's&p 500': { ticker: '^GSPC', name: 'S&P 500' },
    nasdaq: { ticker: '^IXIC', name: 'NASDAQ Composite' },
    dow: { ticker: '^DJI', name: 'Dow Jones Industrial Average' },
    'dow jones': { ticker: '^DJI', name: 'Dow Jones Industrial Average' },
  };

  /**
   * Detect ticker from natural language query with smart caching
   *
   * @param query - User's search query
   * @returns Detection result with ticker, confidence, and suggestions
   */
  async detectTicker(query: string): Promise<TickerDetectionResult> {
    const normalizedQuery = query.toLowerCase().trim();
    const cacheKey = `ticker_detection:${normalizedQuery}`;

    // STEP 0: Check cache first (fastest - ~10ms)
    const cached = await smartCache.get<TickerDetectionResult>(cacheKey);
    if (cached) {
      console.log('✅ Ticker detection cache hit:', cached.ticker);
      return { ...cached, source: 'cache' };
    }

    // STEP 1: Check user's search history (very fast - ~50ms)
    const historyMatch = await this.checkSearchHistory(normalizedQuery);
    if (historyMatch) {
      console.log('✅ Found in search history:', historyMatch.ticker);
      // Cache for future use
      await smartCache.set(cacheKey, historyMatch, { ttl: 24 * 60 * 60 * 1000 }); // 24 hours
      return { ...historyMatch, source: 'history' };
    }

    // STEP 2: Check for explicit ticker patterns
    const explicitTicker = this.extractExplicitTicker(normalizedQuery);
    if (explicitTicker) {
      const result: TickerDetectionResult = {
        ticker: explicitTicker,
        companyName: explicitTicker,
        confidence: 0.95,
        suggestions: [],
        source: 'explicit',
      };
      // Cache explicit patterns
      await smartCache.set(cacheKey, result, { ttl: 24 * 60 * 60 * 1000 });
      return result;
    }

    // STEP 3: Check common mappings (instant)
    const commonMatch = this.checkCommonMappings(normalizedQuery);
    if (commonMatch) {
      const result: TickerDetectionResult = {
        ticker: commonMatch.ticker,
        companyName: commonMatch.name,
        confidence: 0.9,
        suggestions: [],
        source: 'common',
      };
      // Cache common mappings
      await smartCache.set(cacheKey, result, { ttl: 7 * 24 * 60 * 60 * 1000 }); // 7 days
      return result;
    }

    // STEP 3.5: Check database mappings (fast - ~10-20ms) 🆕
    const dbMatch = await this.checkDatabaseMappings(normalizedQuery);
    if (dbMatch) {
      console.log('✅ Found in database:', dbMatch.ticker);
      const result: TickerDetectionResult = {
        ticker: dbMatch.ticker,
        companyName: dbMatch.name,
        confidence: 0.95,
        suggestions: [],
        source: 'common', // Use 'common' source for consistency
      };
      // Cache database results
      await smartCache.set(cacheKey, result, { ttl: 7 * 24 * 60 * 60 * 1000 }); // 7 days
      return result;
    }

    // STEP 4: LLM-based extraction (smart - 200-500ms) 🆕
    console.log('🤖 Using LLM for ticker extraction...');
    const llmResult = await this.extractTickerWithLLM(query);
    if (llmResult && llmResult.ticker && llmResult.confidence >= 0.7) {
      console.log(
        '✅ LLM extracted ticker:',
        llmResult.ticker,
        'confidence:',
        llmResult.confidence
      );
      // Cache LLM results
      await smartCache.set(cacheKey, llmResult, { ttl: 7 * 24 * 60 * 60 * 1000 }); // 7 days
      return { ...llmResult, source: 'llm' };
    }

    // STEP 5: Fallback to Alpha Vantage symbol search (slower - 1-2s)
    console.log('🔍 Falling back to Alpha Vantage API search...');
    const searchResult = await this.searchViaAlphaVantage(query);

    // Cache API results if successful
    if (searchResult.ticker) {
      await smartCache.set(cacheKey, searchResult, { ttl: 7 * 24 * 60 * 60 * 1000 }); // 7 days
    }

    return { ...searchResult, source: 'api' };
  }

  /**
   * Check user's search history for previous searches
   * This provides instant results for stocks the user has searched before
   */
  private async checkSearchHistory(query: string): Promise<TickerDetectionResult | null> {
    try {
      // TODO: Implement proper history lookup using getStockConversationSummary
      // For now, skip history check to avoid errors when no history exists
      return null;
    } catch (error) {
      console.error('Error checking search history:', error);
      return null;
    }
  }

  /**
   * Extract explicit ticker patterns from query
   * Patterns: $TSLA, TSLA, "TSLA", 'TSLA'
   */
  private extractExplicitTicker(query: string): string | null {
    // Pattern 1: $TICKER format (must be uppercase in original query)
    const dollarPattern = /\$([A-Z]{1,5})\b/;
    const dollarMatch = query.match(dollarPattern); // Don't convert to uppercase first!
    if (dollarMatch) {
      return dollarMatch[1];
    }

    // Pattern 2: Standalone uppercase ticker (1-5 letters)
    // e.g., "How is TSLA doing"
    const standalonePattern = /\b([A-Z]{1,5})\b/;
    const standaloneMatch = query.match(standalonePattern); // Don't convert to uppercase first!
    if (standaloneMatch) {
      const possibleTicker = standaloneMatch[1];

      // Exclude common English words that look like tickers
      const excludeWords = [
        'I',
        'A',
        'IS',
        'IT',
        'IN',
        'AT',
        'ON',
        'TO',
        'OR',
        'AND',
        'THE',
        'ME',
        'MY',
        'WE',
        'US',
        'BE',
        'HE',
        'AS',
        'OF',
        'BY',
        'NO',
        'SO',
        'UP',
        'GO',
        'DO',
      ];
      if (!excludeWords.includes(possibleTicker)) {
        return possibleTicker;
      }
    }

    // Pattern 3: Quoted ticker
    const quotedPattern = /["']([A-Za-z]{1,5})["']/;
    const quotedMatch = query.match(quotedPattern);
    if (quotedMatch) {
      return quotedMatch[1].toUpperCase();
    }

    return null;
  }

  /**
   * Check against common company name mappings
   */
  private checkCommonMappings(query: string): { ticker: string; name: string } | null {
    // Direct match
    if (this.commonMappings[query]) {
      return this.commonMappings[query];
    }

    // Partial match - check if query contains any common name
    for (const [companyName, info] of Object.entries(this.commonMappings)) {
      if (query.includes(companyName) || companyName.includes(query)) {
        return info;
      }
    }

    return null;
  }

  /**
   * Check database for company-ticker mappings
   * Fast lookup (~10-20ms) for 1000s of companies
   */
  private async checkDatabaseMappings(
    query: string
  ): Promise<{ ticker: string; name: string } | null> {
    try {
      // Try exact ticker match first (fastest)
      const { data: exactMatch, error: exactError } = await supabase
        .from('company_ticker_mappings')
        .select('ticker, company_name')
        .eq('ticker', query.toUpperCase())
        .eq('is_active', true)
        .maybeSingle();

      if (exactMatch && !exactError) {
        return { ticker: exactMatch.ticker, name: exactMatch.company_name };
      }

      // Try company name match (case-insensitive)
      const { data: nameMatches, error: nameError } = await supabase
        .from('company_ticker_mappings')
        .select('ticker, company_name, market_cap')
        .ilike('company_name_lowercase', `%${query}%`)
        .eq('is_active', true)
        .order('market_cap', { ascending: false, nullsFirst: false })
        .limit(1)
        .maybeSingle();

      if (nameMatches && !nameError) {
        return { ticker: nameMatches.ticker, name: nameMatches.company_name };
      }

      return null;
    } catch (error) {
      console.error('❌ Database mapping lookup error:', error);
      return null;
    }
  }

  /**
   * Search using Alpha Vantage API
   */
  private async searchViaAlphaVantage(query: string): Promise<TickerDetectionResult> {
    try {
      const { data, error } = await stockDataService.searchSymbol(query);

      if (error || !data || data.length === 0) {
        return {
          ticker: null,
          companyName: '',
          confidence: 0,
          suggestions: [],
        };
      }

      const topMatch = data[0];
      const hasMultipleMatches = data.length > 1;

      // If multiple matches with similar scores, return suggestions
      if (hasMultipleMatches && data[1].matchScore >= topMatch.matchScore - 0.1) {
        return {
          ticker: null,
          companyName: '',
          confidence: topMatch.matchScore,
          suggestions: data.slice(0, 5), // Top 5 suggestions
        };
      }

      // Single clear winner
      return {
        ticker: topMatch.symbol,
        companyName: topMatch.name,
        confidence: topMatch.matchScore,
        suggestions: data.slice(1, 4), // Alternative suggestions
      };
    } catch (error) {
      console.error('Alpha Vantage search failed:', error);

      return {
        ticker: null,
        companyName: '',
        confidence: 0,
        suggestions: [],
      };
    }
  }

  /**
   * LLM-based ticker extraction (STEP 4 in hybrid flow)
   * Uses AI to intelligently extract tickers with context awareness
   */
  private async extractTickerWithLLM(query: string): Promise<TickerDetectionResult | null> {
    try {
      const result = await llmTickerExtractor.extractSingleTicker(query);

      if (!result.ticker) {
        return null;
      }

      return {
        ticker: result.ticker,
        companyName: result.ticker, // Will be enriched by API if needed
        confidence: result.confidence,
        suggestions: [],
      };
    } catch (error) {
      console.error('LLM extraction error:', error);
      return null;
    }
  }

  /**
   * Extract multiple tickers from query (for group chat support)
   * @param query - User's query
   * @returns Array of tickers with confidence scores
   */
  async extractMultipleTickers(query: string): Promise<{
    tickers: string[];
    primary: string | null;
    confidence: number;
    context: string;
  }> {
    try {
      const result = await llmTickerExtractor.extractTickers(query);
      return result;
    } catch (error) {
      console.error('Multiple ticker extraction error:', error);
      return {
        tickers: [],
        primary: null,
        confidence: 0,
        context: 'Extraction failed',
      };
    }
  }

  /**
   * Add custom mapping (for user learning over time - future feature)
   */
  addCustomMapping(companyName: string, ticker: string, name: string): void {
    this.commonMappings[companyName.toLowerCase()] = { ticker, name };
  }
}

// Export singleton instance
export const tickerDetectionService = new TickerDetectionService();
